/**
 * When the main chat's active conversation (and its full event history) loads.
 *
 * It is the heaviest payload the app fetches, and only the chat page shows it,
 * so it loads when a surface that shows it mounts — never at boot. The state
 * here is about the store versus the server, not about any one component:
 * `hydrated` says the store already holds (or is getting) the conversation the
 * server considers active, so a re-mount or an `<Activity>` re-show is a no-op,
 * and `reloadRequested` says the server's answer changed under us (login, an
 * activation made elsewhere) and the next surface must fetch it regardless.
 */

import { initializeActiveConversation } from './conversationsStoreIntegration';
import { useAppStore } from './useAppStore';

let hydrated = false;
let reloadRequested = false;
let mountedSurfaces = 0;

/** Fetch the active conversation unless the store already has what a surface needs. */
function ensureActiveConversationLoaded(): void {
  if (hydrated && !reloadRequested) return;
  const forced = reloadRequested;
  hydrated = true;
  reloadRequested = false;
  const { activeConversation, pendingMessage, isLoadingConversation } = useAppStore.getState();
  // A new chat's first message is in flight: the store is ahead of the server.
  if (pendingMessage) return;
  // Picked from a list, or already loading: the store owns its conversation.
  if (!forced && (activeConversation || isLoadingConversation)) return;
  void initializeActiveConversation().then(loaded => {
    // A failed load leaves the store unhydrated, so the next mount retries.
    if (!loaded) hydrated = false;
  });
}

/**
 * Register a mounted surface that shows the active conversation, loading it if
 * the store does not have it yet. Call from an effect; return its cleanup.
 */
export function mountActiveConversationSurface(): () => void {
  mountedSurfaces += 1;
  ensureActiveConversationLoaded();
  return () => {
    mountedSurfaces -= 1;
  };
}

/**
 * The server's active conversation may have changed (login, an activation made
 * outside the store). Reload now if a surface shows it; otherwise on next mount.
 */
export function requestActiveConversationReload(): void {
  reloadRequested = true;
  if (mountedSurfaces > 0) ensureActiveConversationLoaded();
}

/**
 * A sign-in starts a new session, possibly for a different user: drop whatever
 * conversation the store holds so the next chat surface fetches theirs.
 */
export function resetActiveConversationForSignIn(): void {
  useAppStore.getState().clearMessages();
  hydrated = false;
  reloadRequested = false;
}

/** Test seam: forget everything, as a fresh page load would. */
export function resetActiveConversationHydration(): void {
  hydrated = false;
  reloadRequested = false;
  mountedSurfaces = 0;
}
